// api/domainApi.js

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * Domain API Service - All requests go through our backend
 * This keeps GoDaddy API keys secure on the server
 */
export const domainApi = {
  /**
   * Check domain availability
   * @param {string} domain - Domain name to check (e.g., "example.com")
   * @returns {Promise<{available: boolean, domain: string, price?: number, error?: string}>}
   */
  checkAvailability: async (domain) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/domains/check/${encodeURIComponent(domain)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const result = await response.json();

      // Check if the response indicates a service error
      if (result.data?.error || result.data?.serviceOffline) {
        return {
          available: false,
          domain: domain,
          error: result.data?.error || "Domain service is currently offline",
          serviceOffline: true,
          price: null,
          currency: "ZAR",
          suggested: [],
        };
      }

      return {
        available: result.data?.available === true,
        domain: result.data?.domain || domain,
        price: result.data?.price || null,
        currency: result.data?.currency || "ZAR",
        suggested: result.data?.suggested || [],
        simulated: result.data?.simulated || false,
      };
    } catch (error) {
      console.error("Error checking domain availability:", error);
      // Return error state instead of false positive
      return {
        available: false,
        domain: domain,
        error: error.message || "Failed to check domain availability",
        serviceOffline: true,
        price: null,
        currency: "ZAR",
        suggested: [],
      };
    }
  },

  /**
   * Get domain pricing information
   * @param {string} domain - Domain name
   * @returns {Promise<{price: number, currency: string, period: number, error?: string}>}
   */
  getDomainPrice: async (domain) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/domains/price/${encodeURIComponent(domain)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const result = await response.json();

      if (result.data?.error || result.data?.serviceOffline) {
        return {
          price: null,
          currency: "ZAR",
          period: 0,
          error: result.data?.error || "Domain service is currently offline",
          serviceOffline: true,
        };
      }

      return {
        price: result.data?.price || 0,
        currency: result.data?.currency || "ZAR",
        period: result.data?.period || 1,
        simulated: result.data?.simulated || false,
      };
    } catch (error) {
      console.error("Error getting domain price:", error);
      return {
        price: null,
        currency: "ZAR",
        period: 0,
        error: error.message || "Failed to get domain price",
        serviceOffline: true,
      };
    }
  },

  /**
   * Get domain suggestions
   * @param {string} keyword - Search keyword
   * @returns {Promise<{suggestions: Array, error?: string}>}
   */
  getDomainSuggestions: async (keyword) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/domains/suggestions/${encodeURIComponent(keyword)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const result = await response.json();

      if (result.data?.error || result.data?.serviceOffline) {
        return {
          suggestions: [],
          error: result.data?.error || "Domain service is currently offline",
          serviceOffline: true,
        };
      }

      return {
        suggestions: result.data?.suggestions || [],
        simulated: result.data?.simulated || false,
      };
    } catch (error) {
      console.error("Error getting domain suggestions:", error);
      return {
        suggestions: [],
        error: error.message || "Failed to get domain suggestions",
        serviceOffline: true,
      };
    }
  },

  /**
   * Verify domain DNS
   * @param {string} domain - Domain to verify
   * @returns {Promise<{verified: boolean, message: string, error?: string}>}
   */
  verifyDomain: async (domain) => {
    try {
      const response = await fetch(`${API_BASE_URL}/domains/verify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ domain }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const result = await response.json();

      if (result.data?.error || result.data?.serviceOffline) {
        return {
          verified: false,
          message: result.data?.error || "Domain service is currently offline",
          records: [],
          serviceOffline: true,
        };
      }

      return {
        verified: result.data?.verified === true,
        message: result.data?.message || "Domain verification successful",
        records: result.data?.records || [],
        simulated: result.data?.simulated || false,
      };
    } catch (error) {
      console.error("Error verifying domain:", error);
      return {
        verified: false,
        message: error.message || "Unable to verify domain at this time",
        records: [],
        serviceOffline: true,
      };
    }
  },

  /**
   * Register a domain
   * @param {string} domain - Domain name to register
   * @param {object} contactInfo - Contact information
   * @returns {Promise<{success: boolean, orderId: string, domain: string, error?: string}>}
   */
  registerDomain: async (domain, contactInfo) => {
    try {
      const response = await fetch(`${API_BASE_URL}/domains/register`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ domain, contactInfo }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to register domain");
      }

      const result = await response.json();

      if (result.data?.error || result.data?.serviceOffline) {
        return {
          success: false,
          domain: domain,
          error: result.data?.error || "Domain service is currently offline",
          serviceOffline: true,
        };
      }

      return {
        success: true,
        orderId: result.data?.orderId || result.data?.id,
        domain: result.data?.domain || domain,
        status: result.data?.status || "pending",
        simulated: result.data?.simulated || false,
      };
    } catch (error) {
      console.error("Error registering domain:", error);
      return {
        success: false,
        domain: domain,
        error: error.message || "Failed to register domain",
        serviceOffline: true,
      };
    }
  },

  /**
   * Get domain status for a store request
   * @param {string} storeRequestId - Store request ID
   * @returns {Promise<{status: string, domain: string, error?: string}>}
   */
  getDomainStatus: async (storeRequestId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/domains/status/${storeRequestId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const result = await response.json();
      return result.data || null;
    } catch (error) {
      console.error("Error getting domain status:", error);
      return null;
    }
  },

  /**
   * Check SSL certificate
   * @param {string} domain - Domain to check
   * @returns {Promise<{valid: boolean, daysRemaining: number, error?: string}>}
   */
  checkSSL: async (domain) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/domains/ssl/${encodeURIComponent(domain)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const result = await response.json();
      return result.data || null;
    } catch (error) {
      console.error("Error checking SSL:", error);
      return null;
    }
  },
};
